export const FPR_FLOOR_EPS = 0.1;
export const FPR_FUND_LAG = 1;
export const FPR_PAY_LAG = 2;
export const FPR_MAX_MONTHS = 120;

export const DEFAULT_FPR_INPUTS = {
  anchors: [
    { month: 0, roasPct: 12 },
    { month: 4, roasPct: 40 },
    { month: 14, roasPct: 100 },
  ],
  momStep: 3,
  momMode: 'PP',
  capPct: 105,
  borderlinePct: 80,
  fundingPct: 80,
  revenueSharePct: 60,
  monthlyXirrPct: 1.2,
  uaAmount: 1000000,
  commitmentAmount: 15000000,
  fundingMonths: 24,
  accelerationPct: 100,
};

function finiteNumber(value, fallback) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function clampShare(pct, fallback) {
  const num = finiteNumber(pct, fallback);
  return Math.min(1, Math.max(0, num / 100));
}

export function normalizeFprInputs(raw = {}) {
  const defaults = DEFAULT_FPR_INPUTS;
  const anchors = (Array.isArray(raw.anchors) ? raw.anchors : defaults.anchors)
    .map((anchor) => ({
      month: Math.max(0, Math.round(finiteNumber(anchor.month, 0))),
      roasPct: Math.max(0, finiteNumber(anchor.roasPct, 0)),
    }))
    .sort((left, right) => left.month - right.month);

  const uniqueAnchors = [];
  const seen = new Set();
  anchors.forEach((anchor) => {
    if (seen.has(anchor.month)) {
      return;
    }
    seen.add(anchor.month);
    uniqueAnchors.push(anchor);
  });

  const fundingMonths = Math.max(
    1,
    Math.round(finiteNumber(raw.fundingMonths, defaults.fundingMonths))
  );
  const lastAnchorMonth =
    uniqueAnchors.length > 0 ? uniqueAnchors[uniqueAnchors.length - 1].month : 0;

  return {
    anchors: uniqueAnchors,
    momStep: Math.max(0, finiteNumber(raw.momStep, defaults.momStep)),
    momMode: raw.momMode === 'REL' ? 'REL' : 'PP',
    capPct: Math.max(0, finiteNumber(raw.capPct, defaults.capPct)),
    borderlineShare: clampShare(raw.borderlinePct, defaults.borderlinePct),
    fundingShare: clampShare(raw.fundingPct, defaults.fundingPct),
    revenueShare: clampShare(raw.revenueSharePct, defaults.revenueSharePct),
    monthlyXirr: finiteNumber(raw.monthlyXirrPct, defaults.monthlyXirrPct) / 100,
    uaAmount: Math.max(1, finiteNumber(raw.uaAmount, defaults.uaAmount)),
    commitmentAmount: Math.max(
      1,
      finiteNumber(raw.commitmentAmount, defaults.commitmentAmount)
    ),
    fundingMonths,
    horizon: lastAnchorMonth,
    accelerationShare: clampShare(raw.accelerationPct, defaults.accelerationPct),
  };
}

export function requiredMomStep(cumulativeRoas, inputs) {
  if (cumulativeRoas >= inputs.capPct) {
    return 0;
  }
  if (inputs.momMode === 'REL') {
    return (cumulativeRoas * inputs.momStep) / 100;
  }
  return inputs.momStep;
}

export function borderlineFloor(requirement, borderlineShare) {
  return borderlineShare * requirement + FPR_FLOOR_EPS;
}

export function resolveMissedAnchorMonths(strategy = {}, laterMonths = []) {
  if (Array.isArray(strategy.missAnchorMonths)) {
    return [...new Set(strategy.missAnchorMonths.filter((month) => month > 0))].sort(
      (left, right) => left - right
    );
  }
  if (strategy.missAnchorMonth === 'all-later') {
    return laterMonths.filter((month) => month > 0);
  }
  if (strategy.missAnchorMonth == null) {
    return [];
  }
  return strategy.missAnchorMonth > 0 ? [strategy.missAnchorMonth] : [];
}

export function anchorRequirement(month, anchorRoas, borderlineShare, strategy = {}, laterMonths = []) {
  if (month === 0) {
    return anchorRoas;
  }
  const missedMonths = resolveMissedAnchorMonths(strategy, laterMonths);
  if (missedMonths.includes(month)) {
    return borderlineFloor(anchorRoas, borderlineShare);
  }
  return anchorRoas;
}

export function derivedCollectionShare(inputs) {
  return inputs.fundingShare * inputs.revenueShare;
}

export function derivedReferenceShare(inputs) {
  return inputs.revenueShare;
}

export function latestDueAnchorRoas(month, anchors) {
  let dueRoas = null;
  anchors.forEach((anchor) => {
    if (anchor.month <= month) {
      dueRoas = anchor.roasPct;
    }
  });
  return dueRoas;
}

export function solveWorstRoasVector(inputs, strategy = { missAnchorMonth: 'all-later', momUsesFloor: true }) {
  const params = normalizeFprInputs(inputs);
  const anchorsByMonth = {};
  params.anchors.forEach((anchor) => {
    anchorsByMonth[anchor.month] = anchor.roasPct;
  });
  const laterAnchorMonths = params.anchors.map((anchor) => anchor.month);

  let cumulativeRoas = 0;
  let previousStep = Infinity;
  let infeasible = null;
  let anchorAccelerated = false;
  const referenceShare = Math.max(derivedReferenceShare(params), 1e-9);
  const lastCovenantMonth = laterAnchorMonths.length > 0 ? laterAnchorMonths[laterAnchorMonths.length - 1] : 0;
  const rows = [];

  for (let month = 0; month <= FPR_MAX_MONTHS; month += 1) {
    let lowestStep = 0;
    let bind = 'nothing holds this month';
    const fullMom = month > 0 ? requiredMomStep(cumulativeRoas, params) : 0;

    if (fullMom > 0) {
      const momNeed = strategy.momUsesFloor
        ? borderlineFloor(fullMom, params.borderlineShare)
        : fullMom;
      if (momNeed > lowestStep) {
        lowestStep = momNeed;
        bind = strategy.momUsesFloor
          ? 'the month-to-month borderline'
          : 'the month-to-month minimum';
      }
    }

    if (anchorsByMonth[month] !== undefined) {
      const neededForAnchor =
        anchorRequirement(
          month,
          anchorsByMonth[month],
          params.borderlineShare,
          strategy,
          laterAnchorMonths
        ) - cumulativeRoas;
      if (neededForAnchor > lowestStep) {
        lowestStep = neededForAnchor;
        bind =
          month === 0
            ? 'the Month 0 anchor'
            : `the Month ${month} anchor borderline`;
      }
    }

    laterAnchorMonths.forEach((anchorMonth) => {
      if (anchorMonth <= month) {
        return;
      }
      const neededToReach =
        (anchorRequirement(
          anchorMonth,
          anchorsByMonth[anchorMonth],
          params.borderlineShare,
          strategy,
          laterAnchorMonths
        ) -
          cumulativeRoas) /
        (anchorMonth - month + 1);
      if (neededToReach > lowestStep + 1e-12) {
        lowestStep = neededToReach;
        bind =
          anchorMonth === 0
            ? 'still being able to reach the Month 0 anchor'
            : `still being able to reach the Month ${anchorMonth} borderline`;
      }
    });

    if (lowestStep > previousStep + 1e-9 && infeasible === null) {
      infeasible = { month, need: lowestStep, previousStep };
    }

    const step = Math.max(0, lowestStep);
    cumulativeRoas += step;

    const fullAnchor =
      anchorsByMonth[month] !== undefined ? anchorsByMonth[month] : null;
    const dueAnchorRoas = latestDueAnchorRoas(month, params.anchors);
    const momBreach = fullMom > 0 && step + 1e-9 < fullMom;
    const missedThisAnchor =
      fullAnchor !== null && cumulativeRoas + 1e-9 < fullAnchor;
    if (fullAnchor !== null) {
      anchorAccelerated = missedThisAnchor;
    }
    const momRecall = fullMom > 0 && step <= params.borderlineShare * fullMom + 1e-12;
    const anchorRecall =
      fullAnchor !== null &&
      cumulativeRoas <= params.borderlineShare * fullAnchor + 1e-12;

    rows.push({
      month,
      step,
      cumulativeRoas,
      referenceStep: step,
      referenceRoas: cumulativeRoas,
      clientStep: step / referenceShare,
      clientRoas: cumulativeRoas / referenceShare,
      bind,
      isAnchor: fullAnchor !== null,
      fullMom,
      fullAnchor,
      dueAnchorRoas,
      momBreach,
      underAnchor: missedThisAnchor,
      anchorBreach: missedThisAnchor,
      anchorAccelerated,
      accelerated: momBreach || anchorAccelerated,
      recall: momRecall || anchorRecall,
    });
    previousStep = step;

    if (month < lastCovenantMonth) {
      continue;
    }
    const ledger = ledgerCollections(rows, params);
    if (ledger.closedMonth != null) {
      break;
    }
    if (step === 0 && month > lastCovenantMonth) {
      break;
    }
  }

  return { rows, infeasible, inputs: params };
}

export function monthlyCohortRevenue(step, uaAmount) {
  return (Math.max(0, step) / 100) * uaAmount;
}

export function fundedPrincipal(inputs) {
  const params = normalizeFprInputs(inputs);
  return Math.max(params.uaAmount * params.fundingShare, 1e-9);
}

export function ledgerCollections(rows, inputs) {
  const params = normalizeFprInputs(inputs);
  const normalShare = derivedCollectionShare(params);
  const principal = fundedPrincipal(params);
  const lastTick = rows.length + FPR_PAY_LAG + 10;

  let balance = 0;
  let collected = 0;
  let moneyBackMonth = null;
  let closedMonth = null;
  const ledgerRows = [];

  for (let tick = 0; tick <= lastTick; tick += 1) {
    if (tick === FPR_FUND_LAG) {
      balance = principal;
    } else if (tick > FPR_FUND_LAG && balance > 1e-6) {
      balance *= 1 + params.monthlyXirr;
    }

    const cohortMonth = tick - FPR_PAY_LAG;
    let pay = 0;
    let share = null;
    if (cohortMonth >= 0 && cohortMonth < rows.length) {
      const accelerated = Boolean(rows[cohortMonth].accelerated);
      share = accelerated ? params.accelerationShare : normalShare;
      if (balance > 1e-6) {
        const clientRevenue = monthlyCohortRevenue(
          rows[cohortMonth].clientStep,
          params.uaAmount
        );
        pay = Math.min(clientRevenue * share, clientRevenue, balance);
        balance -= pay;
        collected += pay;
        if (moneyBackMonth === null && collected >= principal - 1e-6) {
          moneyBackMonth = cohortMonth;
        }
      }
    }

    if (
      closedMonth === null &&
      tick > FPR_FUND_LAG &&
      collected > 0 &&
      balance <= 1e-6
    ) {
      closedMonth = cohortMonth >= 0 ? cohortMonth : 0;
    }

    if (cohortMonth >= 0 && cohortMonth < rows.length) {
      ledgerRows[cohortMonth] = {
        share,
        pay,
        cumulativeCollected: collected,
        collectionRoas: (collected / params.uaAmount) * 100,
        treisarRoas: (collected / principal) * 100,
        remainingBalance: Math.max(0, balance),
      };
    }
  }

  return {
    rows: ledgerRows,
    moneyBackMonth,
    closedMonth,
    total: collected,
    multiple: collected / principal,
    fundedAmount: principal,
  };
}

export function lastMissableAnchor(inputs) {
  const params = normalizeFprInputs(inputs);
  return [...params.anchors].reverse().find((anchor) => anchor.month > 0) || null;
}

export function laterBreakAnchorMonths(inputs) {
  return normalizeFprInputs(inputs)
    .anchors.filter((anchor) => anchor.month > 0)
    .map((anchor) => anchor.month);
}

function eachSubset(items) {
  const subsets = [[]];
  items.forEach((item) => {
    const grown = subsets.map((subset) => [...subset, item]);
    subsets.push(...grown);
  });
  return subsets;
}

export function listPaybackStrategies(inputs) {
  const laterMonths = laterBreakAnchorMonths(inputs);
  const strategies = [];
  eachSubset(laterMonths).forEach((missAnchorMonths) => {
    strategies.push({ missAnchorMonths, momUsesFloor: false });
    strategies.push({ missAnchorMonths, momUsesFloor: true });
  });
  return strategies;
}

function isFullyRepaid(ledger) {
  return ledger.closedMonth != null;
}

function firstAccelMonth(rows) {
  const row = rows.find((item) => item.accelerated);
  return row ? row.month : Number.POSITIVE_INFINITY;
}

function isWorsePayback(candidate, winner) {
  const candidateRepays = isFullyRepaid(candidate.ledger);
  const winnerRepays = isFullyRepaid(winner.ledger);
  if (candidateRepays !== winnerRepays) {
    return candidateRepays;
  }
  if (!candidateRepays) {
    return candidate.ledger.total > winner.ledger.total;
  }
  if (candidate.ledger.closedMonth !== winner.ledger.closedMonth) {
    return candidate.ledger.closedMonth > winner.ledger.closedMonth;
  }
  const candidateAccel = firstAccelMonth(candidate.rows);
  const winnerAccel = firstAccelMonth(winner.rows);
  if (candidateAccel !== winnerAccel) {
    return candidateAccel > winnerAccel;
  }
  const candidateBack = candidate.ledger.moneyBackMonth ?? -1;
  const winnerBack = winner.ledger.moneyBackMonth ?? -1;
  if (candidateBack !== winnerBack) {
    return candidateBack > winnerBack;
  }
  return candidate.ledger.total < winner.ledger.total;
}

function summarizeCandidate(solved, ledger, strategy) {
  const broken = solved.rows
    .filter((row) => row.isAnchor && row.month > 0 && row.anchorBreach && !row.recall)
    .map((row) => row.month);
  const firstAccel = solved.rows.find((row) => row.accelerated);
  const lastAnchorRow = [...solved.rows].reverse().find((row) => row.isAnchor && row.month > 0);
  return {
    missAnchorMonths: resolveMissedAnchorMonths(strategy),
    momUsesFloor: Boolean(strategy.momUsesFloor),
    closedMonth: ledger.closedMonth,
    recalled: solved.rows.some((row) => row.recall),
    brokenMonths: broken,
    firstAccelMonth: firstAccel ? firstAccel.month : null,
    lastAnchorMonth: lastAnchorRow ? lastAnchorRow.month : null,
    lastAnchorFull: lastAnchorRow ? lastAnchorRow.fullAnchor : null,
    lastAnchorReference: lastAnchorRow ? lastAnchorRow.referenceRoas : null,
    lastAnchorBreach: lastAnchorRow ? Boolean(lastAnchorRow.anchorBreach) : false,
  };
}

export function describeWorstPath(winner, candidates = []) {
  const miss = (winner.strategy && winner.strategy.missAnchorMonths) || [];
  const lastMonth =
    winner.inputs != null
      ? laterBreakAnchorMonths(winner.inputs).slice(-1)[0]
      : null;
  const lines = [];

  lines.push('Month 0 is a hard gate and was never broken.');

  if (miss.length === 0) {
    lines.push('The winning mix met every later anchor in full.');
  } else {
    const momPart =
      winner.strategy && winner.strategy.momUsesFloor
        ? 'MoM sat on the borderline floor (a MoM break).'
        : 'MoM stayed at the full minimum (no MoM break).';
    lines.push(
      `The winning mix broke later anchors at month ${miss.join(', month ')}. ${momPart}`
    );
  }

  if (winner.ledger.closedMonth == null) {
    lines.push('This mix did not fully repay within the path.');
  } else {
    lines.push(
      `This mix was used because it took the longest to repay: close at month ${winner.ledger.closedMonth}.`
    );
  }

  const lastOnly = candidates.filter(
    (item) =>
      lastMonth != null &&
      item.missAnchorMonths.length === 1 &&
      item.missAnchorMonths[0] === lastMonth
  );
  lastOnly.forEach((item) => {
    const momLabel = item.momUsesFloor ? 'MoM on the floor' : 'full MoM';
    if (item.recalled) {
      lines.push(
        `A break at only month ${lastMonth} with ${momLabel} was a recall, so it was thrown out.`
      );
      return;
    }
    if (!item.lastAnchorBreach && item.lastAnchorFull != null) {
      lines.push(
        `A break at only month ${lastMonth} with ${momLabel} never registered. By then reference revenue was already ${item.lastAnchorReference.toFixed(2)}%, which meets the ${item.lastAnchorFull}% bar, because an earlier full anchor plus MoM had already gotten there.`
      );
      return;
    }
    if (item.closedMonth == null) {
      lines.push(
        `A break at only month ${lastMonth} with ${momLabel} did not fully repay, so it was not used.`
      );
      return;
    }
    lines.push(
      `A break at only month ${lastMonth} with ${momLabel} did register, but repaid earlier (month ${item.closedMonth}), so it was not the longest path.`
    );
  });

  return lines;
}

export function runFprSolver(rawInputs) {
  const strategies = listPaybackStrategies(rawInputs);
  const candidates = [];
  let winner = null;
  strategies.forEach((strategy) => {
    const solved = solveWorstRoasVector(rawInputs, strategy);
    const ledger = ledgerCollections(solved.rows, solved.inputs);
    const summary = summarizeCandidate(solved, ledger, strategy);
    candidates.push(summary);
    if (summary.recalled) {
      return;
    }
    const candidate = {
      ...solved,
      ledger,
      strategy,
    };
    if (winner == null || isWorsePayback(candidate, winner)) {
      winner = candidate;
    }
  });

  if (winner == null) {
    const fallbackStrategy = strategies[0] || {
      missAnchorMonths: [],
      momUsesFloor: false,
    };
    const solved = solveWorstRoasVector(rawInputs, fallbackStrategy);
    winner = {
      ...solved,
      ledger: ledgerCollections(solved.rows, solved.inputs),
      strategy: fallbackStrategy,
    };
  }

  const explanation = describeWorstPath(winner, candidates);

  return {
    inputs: winner.inputs,
    rows: winner.rows,
    infeasible: winner.infeasible,
    ledger: winner.ledger,
    strategy: winner.strategy,
    explanation,
    candidates,
  };
}
